/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useState } from 'react'
import Button from '@components/common/Button/Button.component'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import { Claim, Procedure, Attachment } from '@dataTypes/claimFeature.types'
import { Form, Table } from 'react-bootstrap'
import { getFileIcon } from '@utils/file.utils'
import { formatDate } from '@utils/date.utils'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import FileViewer from '../FileViewerModal/FileViewer.component'
import { useNoteEditorOverlay } from '@components/common/NoteEditorOverlay'
import type { NoteMutationSuccessDetail } from '@components/common/NoteEditorOverlay/notesFeature.types'
import {
  attachmentTypeOptions,
  getAttachmentType,
  getClaimIdForNotesHeader,
  getNotesClaimContextId,
} from '@utils/claims.util'
import { htmlToPlainTextPreview } from '@utils/htmlPlainText.util'
import { formatNoteUpdaterInitialLast } from '@utils/noteSidebarAuthor.util'
import { formatNoteTitleWithVersion } from '@utils/noteVersion.util'
import EditClaimProcedureModal from '../EditClaimProcedureModal/EditClaimProcedureModal.component'
import moment from 'moment'
import { INote, NoteContextType } from '@dataTypes/note.type'
import type { Patient } from '@dataTypes/PatientInfo.types'
import { fetchPatientById } from '@services/api/patient.api'
import { getAllNotes } from '@services/api/note.api'
import { useAppSelector } from '@store/hooks'

type ClaimReviewTabProps = {
  claimData: Claim
  onEditClick: () => void
  onClaimNotesSynced?: (notes: INote[]) => void
}

const ClaimReviewTab: React.FC<ClaimReviewTabProps> = ({
  claimData,
  onEditClick,
  onClaimNotesSynced,
}) => {
  const subscriberName = claimData.subscriberName || '-'
  const subscriberId = claimData.subscriberId || '-'
  const [expandedRow, setExpandedRow] = useState<string>('details')
  const [fileViewerInfo, setFileViewerInfo] = useState<{
    title: string
    show: boolean
    file: File
    url: string
  }>({
    title: '',
    show: false,
    file: {} as File,
    url: '',
  })
  const { openNoteEditor, updateNoteEditor, closeNoteEditor } =
    useNoteEditorOverlay()
  const [showViewProcedureModal, setShowViewProcedureModal] =
    useState<boolean>(false)
  const [selectedProcedure, setSelectedProcedure] = useState<Procedure | null>(
    null,
  )
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const loggedInUser = useAppSelector(
    (state) => state.chatInternal.loggedInUser,
  )
  const user = JSON.parse(localStorage.getItem('user') || '{}')

  const handleAccordionClick = (section: string): void => {
    setExpandedRow((prev) => (prev === section ? '' : section))
  }

  const handleViewAttachment = (attachment: Attachment): void => {
    setFileViewerInfo({
      title: `Viewing: ${attachment.item}`,
      show: true,
      file: attachment.newFile!,
      url: attachment.fileUrl!,
    })
  }

  const procedureColumns = [
    {
      field: 'procedureName',
      headerText: 'Procedure',
      template: (rowData: Procedure) => {
        let isReqCompleted = true
        const requirements =
          claimData.attachmentRequirements?.requirements.filter(
            (requirement: any) =>
              requirement.procedureCode === rowData.procedureCode,
          ) || []
        if (requirements.length > 0 && requirements[0].required) {
          Object.keys(requirements[0].required).forEach((key) => {
            if (requirements[0].required[key] === true) {
              if (
                rowData.procedureAttachments.find(
                  (attachment: any) =>
                    attachment.attachmentType === getAttachmentType(key),
                ) !== undefined
              ) {
                console.log('key', key)
              } else {
                isReqCompleted = false
              }
            }
          })
        }
        return (
          <div style={{ whiteSpace: 'normal' }}>
            <div className="procedure-cell d-flex align-items-center">
              <span
                className={`icon ${isReqCompleted ? 'mgc_save_line procedure-attachment-complete-icon' : 'mgc_file_warning_line procedure-attachment-incomplete-icon'}`}
              ></span>
              <span className="mx-1">{rowData.procedureCode}</span>
              <span className="icon mgc_information_fill procedure-info-icon"></span>
            </div>
          </div>
        )
      },
      width: '100',
      headerStyle: { textAlign: 'center' },
    },
    { field: 'procedureDate', headerText: 'Procedure date', width: '180' },
    {
      field: 'siteRegion',
      headerText: 'T/S',
      width: '180',
      headerStyle: { textAlign: 'center' },
      template: (data: Procedure) => {
        return (
          <span style={{ whiteSpace: 'normal' }}>{data.siteRegion || '-'}</span>
        )
      },
    },
    {
      field: 'amount',
      headerText: 'Claim amt.',
      template: (rowData: Procedure) => (
        <div>
          <div>{`$${Number(rowData.amount).toFixed(2)}`}</div>
        </div>
      ),
      width: 'auto',
    },
    {
      field: 'actions',
      headerText: 'Actions',
      headerStyle: { textAlign: 'center' },
      template: (rowData: Procedure) => (
        <ActionButtons
          onView={() => {
            setSelectedProcedure(rowData)
            setShowViewProcedureModal(true)
          }}
          showMoreOptions={false}
        />
      ),
      width: '110',
    },
  ]

  const attachmentColumns = [
    { field: 'itemNo', headerText: 'Item #', width: '70px' },
    {
      field: 'item',
      headerText: 'Item',
      width: '70px',
      template: (rowData: any) => (
        <div
          className="align-items-center justify-content-center"
          title={rowData.item}
        >
          <i className={`${getFileIcon(rowData.item)} file-icon fs-1`} />
        </div>
      ),
    },
    {
      field: 'formatType',
      headerText: 'Format type',
      width: '100px',
      template: (rowData: Attachment) => (
        <span>{rowData.formatType.toString().toUpperCase()}</span>
      ),
    },
    {
      field: 'attachmentType',
      headerText: 'Attachment type',
      width: '200px',
      template: (rowData: Attachment): JSX.Element => (
        <span>
          {attachmentTypeOptions.find(
            (option) => option.value === rowData.attachmentType,
          )?.label || 'N/A'}
        </span>
      ),
    },
    {
      field: 'dateTaken',
      headerText: 'Date taken',
      width: '80px',
      template: (rowData: any) => <span>{formatDate(rowData.dateTaken)}</span>,
    },
    {
      field: 'actions',
      headerText: 'Actions',
      width: '70px',
      template: (rowData: any) => (
        <ActionButtons
          onView={() => handleViewAttachment(rowData)}
          showMoreOptions={false}
        />
      ),
    },
  ]

  const handleClaimNoteMutationSuccess = useCallback(
    (detail?: NoteMutationSuccessDetail): void => {
      if (!onClaimNotesSynced) return
      const patientId = claimData.patientIdSystem ?? claimData.patientId
      if (!patientId) return
      const contextId = getNotesClaimContextId(claimData)
      if (!contextId) return
      const pushNotes = onClaimNotesSynced

      const matchesClaimContext = (n: INote): boolean =>
        n.context_type === NoteContextType.CLAIMS &&
        n.context_id != null &&
        String(n.context_id) === String(contextId)

      const readScoped = async (): Promise<INote[]> => {
        const scoped = await getAllNotes({
          patientId,
          contextType: NoteContextType.CLAIMS,
          contextId,
        })
        if (scoped.statusCode !== 200) return []
        return scoped.data?.notes ?? []
      }

      const readFilteredFallback = async (): Promise<INote[]> => {
        const allPatient = await getAllNotes({ patientId })
        if (allPatient.statusCode !== 200) return []
        return (allPatient.data?.notes ?? []).filter(matchesClaimContext)
      }

      const delay = (ms: number): Promise<void> =>
        new Promise((resolve) => {
          setTimeout(resolve, ms)
        })

      void (async (): Promise<void> => {
        let list = await readScoped()
        if (list.length === 0) {
          list = await readFilteredFallback()
        }

        const saved = detail?.savedNote
        let needSavedId: string | undefined
        if (saved != null && matchesClaimContext(saved)) {
          needSavedId = saved.note_id
        }

        if (
          needSavedId != null &&
          !list.some((n) => n.note_id === needSavedId)
        ) {
          for (let attempt = 0; attempt < 2; attempt += 1) {
            await delay(400)
            list = await readScoped()
            if (list.length === 0) {
              list = await readFilteredFallback()
            }
            if (list.some((n) => n.note_id === needSavedId)) break
          }
        }

        if (saved != null && matchesClaimContext(saved)) {
          if (!list.some((n) => n.note_id === saved.note_id)) {
            list = [...list, saved]
          }
        }

        pushNotes(list)
      })()
    },
    [claimData, onClaimNotesSynced],
  )

  const handleViewNote = (note: Partial<INote>): void => {
    const patientId = claimData.patientIdSystem ?? claimData.patientId
    const claimTitleContext = getClaimIdForNotesHeader(claimData) ?? 'Claim'
    const contextId =
      (typeof note.context_id === 'string' && note.context_id.trim()) ||
      claimData.claim_id ||
      getNotesClaimContextId(claimData)
    const openNoteId =
      typeof note.note_id === 'string' && note.note_id.trim()
        ? note.note_id
        : undefined

    const matchedPatient: Patient | null =
      selectedPatient?.patient_id === patientId
        ? (selectedPatient as unknown as Patient)
        : null

    openNoteEditor({
      patient: matchedPatient,
      contextId: contextId ?? null,
      contextType: NoteContextType.CLAIMS,
      openNoteId: openNoteId ?? null,
      contextName: claimTitleContext,
      openFirstNote: true,
      initialView: 'list',
      hideNotesTable: true,
      overlayBackLabel: 'Back',
      editorBackButtonLabel: 'Back',
      openContextNotesByDefault: true,
      notesFetchLoadingContainer: 'body',
      portalTarget: '.page-content',
      anchorClassName: 'claims-note-editor-overlay-anchor',
      claimStatusForNotes: claimData.generalStatus ?? claimData.status,
      onNoteMutationSuccess: handleClaimNoteMutationSuccess,
    })

    if (matchedPatient) return

    void fetchPatientById(patientId)
      .then((response) => {
        updateNoteEditor({
          patient: response.data.patient as unknown as Patient,
        })
      })
      .catch(() => {
        closeNoteEditor()
      })
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pb-1">
        <h4 className="claim-review-title">Review the information below</h4>
        <Button size="sm" className="button-tertiary" onClick={onEditClick}>
          <i className="ti ti-pencil"></i> Edit
        </Button>
      </div>

      <div className="accordion" id="accordionReview">
        {/* Claim Details */}
        <div className="accordion-item">
          <h2 className="ac-header">
            <button
              className={`accordion-button ${expandedRow === 'details' || expandedRow === 'all' ? '' : 'collapsed'}`}
              type="button"
              onClick={() => handleAccordionClick('details')}
            >
              Claim details
            </button>
          </h2>
          <div
            id="details"
            className={`accordion-collapse collapse ${expandedRow === 'details' || expandedRow === 'all' ? 'show' : ''}`}
          >
            <div className="accordion-body">
              {[
                {
                  label: 'Patient name',
                  value: claimData.patientName,
                  id: null,
                },
                {
                  label: 'Subscriber',
                  value: subscriberName,
                  id: subscriberId,
                },
                {
                  label: 'Payer',
                  value: claimData.insuranceCarrier,
                  id: claimData.insuranceCarrierPayerCode,
                },
                {
                  label: 'Provider',
                  value: claimData.providerName,
                  id: claimData.providerTaxId,
                },
                {
                  label: 'Entity type',
                  value: claimData.entityType,
                  id: null,
                },
                {
                  label: 'Item type',
                  value: claimData.claimType,
                  id: null,
                },
              ].map((detail, index) => (
                <div className="row py-1" key={index}>
                  <div className="col col-review-label">{detail.label}</div>
                  <div className="col col-review-value text-end">
                    {detail.value}
                    {detail.id && (
                      <small className="ms-1 text-muted">{detail.id}</small>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Procedures */}
        <div className="accordion-item">
          <h2 className="ac-header">
            <button
              className={`accordion-button ${expandedRow === 'procedures' || expandedRow === 'all' ? '' : 'collapsed'}`}
              type="button"
              onClick={() => handleAccordionClick('procedures')}
            >
              Procedures
            </button>
          </h2>
          <div
            id="procedures"
            className={`accordion-collapse collapse ${expandedRow === 'procedures' || expandedRow === 'all' ? 'show' : ''}`}
          >
            <div className="accordion-body p-2">
              <DataGridComponent
                data={claimData.procedures || []}
                columns={procedureColumns}
                rowHeight={36}
                allowPaging={true}
                pageSize={10}
                showCheckbox={false}
                customCSS="claim-procedure-table-review"
              />
            </div>
          </div>
        </div>

        {/* Attachments */}
        <div className="accordion-item">
          <h2 className="ac-header">
            <button
              className={`accordion-button ${expandedRow === 'attachments' || expandedRow === 'all' ? '' : 'collapsed'}`}
              type="button"
              onClick={() => handleAccordionClick('attachments')}
            >
              Attachments
            </button>
          </h2>
          <div
            id="attachments"
            className={`accordion-collapse collapse ${expandedRow === 'attachments' || expandedRow === 'all' ? 'show' : ''}`}
          >
            <div className="accordion-body p-2">
              <DataGridComponent
                data={claimData.attachments || []}
                columns={attachmentColumns}
                rowHeight={36}
                showCheckbox={false}
                customCSS="claim-attachment-table-review"
              />
            </div>
            <div className="note-section-review p-2 pt-0">
              {(claimData.notes?.length ?? 0) > 0 && (
                <Form.Group>
                  <Form.Label>Notes</Form.Label>
                  <div className="table-responsive mt-2 claim-review-note-table-scroll">
                    <Table className="proc-table note-table">
                      <tbody>
                        {(claimData.notes || []).map((n, i) => {
                          const authorDisplay =
                            formatNoteUpdaterInitialLast(
                              n.updatedBy?.first_name,
                              n.updatedBy?.last_name,
                            ) ||
                            formatNoteUpdaterInitialLast(
                              user.first_name,
                              user.last_name,
                            ) ||
                            formatNoteUpdaterInitialLast(
                              loggedInUser?.first_name,
                              loggedInUser?.last_name,
                            ) ||
                            user.username
                          const previewText = htmlToPlainTextPreview(
                            n.body ?? (n as { content?: string }).content ?? '',
                          )
                          const noteDateRaw =
                            n.date_time_updated_at ?? n.date_time_created_at
                          const noteDateDisplay =
                            noteDateRaw != null &&
                            String(noteDateRaw).trim() !== '' &&
                            moment(noteDateRaw).isValid()
                              ? moment(noteDateRaw).format(
                                  'MM-DD-YYYY - hh:mm A',
                                )
                              : ''
                          return (
                            <tr key={i}>
                              <td className="p-2">
                                <div
                                  key={i}
                                  className="d-flex justify-content-between align-items-start"
                                >
                                  <div className="flex-grow-1 min-w-0">
                                    <div className="d-flex justify-content-between align-items-baseline gap-3 note-table-note-topline">
                                      <div className="note-table-title-by text-truncate min-w-0">
                                        <span className="note-table-title-text">
                                          {formatNoteTitleWithVersion(
                                            {
                                              title: n.title ?? '',
                                              version: n.version,
                                              is_latest: n.is_latest,
                                              note_group_id: n.note_group_id,
                                              patient_id:
                                                n.patient_id ??
                                                claimData.patientIdSystem ??
                                                claimData.patientId ??
                                                '',
                                              contact_id: n.contact_id ?? '',
                                              context_type:
                                                n.context_type ??
                                                NoteContextType.CLAIMS,
                                              context_id:
                                                n.context_id ??
                                                claimData.claim_id ??
                                                getNotesClaimContextId(
                                                  claimData,
                                                ) ??
                                                '',
                                            },
                                            {
                                              versionChainPool: claimData.notes,
                                            },
                                          )}
                                        </span>
                                        <span className="note-table-by-sep">
                                          {' '}
                                          by{' '}
                                        </span>
                                        <span className="note-table-author-text">
                                          {authorDisplay}
                                        </span>
                                      </div>
                                      {noteDateDisplay ? (
                                        <span className="small text-muted flex-shrink-0">
                                          {noteDateDisplay}
                                        </span>
                                      ) : null}
                                    </div>
                                    {previewText ? (
                                      <div className="note-table-preview">
                                        {previewText}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  </div>
                </Form.Group>
              )}
            </div>
          </div>
        </div>
        {/* */}
      </div>
      {fileViewerInfo.show && (
        <FileViewer
          show={fileViewerInfo.show}
          onHide={() => setFileViewerInfo({ ...fileViewerInfo, show: false })}
          title={fileViewerInfo.title}
          {...(fileViewerInfo.file && { sourcefile: fileViewerInfo.file })}
          {...(fileViewerInfo.url && { sourceUrl: fileViewerInfo.url })}
        />
      )}
      {showViewProcedureModal && selectedProcedure && (
        <EditClaimProcedureModal
          isVisible={showViewProcedureModal}
          editMode={false}
          closeModal={() => setShowViewProcedureModal(false)}
          procedure={selectedProcedure}
          selectedClaim={claimData}
          onSave={() => {}}
        />
      )}
    </>
  )
}

export default React.memo(ClaimReviewTab)
